/**
 * Bilaga references from `attachment` slots (legacy `attachment_ref`
 * fields, "Pattern B"). A slot holds a bilaga id (or ids); the id resolves
 * against the host's attachments on the compile context, and the assembler
 * numbers whatever the blocks declare.
 */
import { attachmentList, attachmentRef, p, t, val } from '@hyranai/regta-document-ir';
export const attachmentIds = (value) => {
    if (typeof value === 'string')
        return value.length > 0 ? [value] : [];
    if (Array.isArray(value))
        return value.filter((x) => typeof x === 'string' && x.length > 0);
    return [];
};
export const hasAttachment = (value) => attachmentIds(value).length > 0;
const declOf = (a) => ({
    id: a.id,
    label: a.label,
    ...(a.fixedNumber != null ? { fixedNumber: a.fixedNumber } : {}),
    ...(a.pending ? { pending: true } : {}),
});
/**
 * Every attachment the host supplies, in its order. A block that refers to
 * a bilaga declares the whole set so the assembler numbers them in the
 * contract's order regardless of which block compiles first.
 */
export const contextAttachments = (ctx) => ctx.attachments.map(declOf);
/** The host attachments a slot value refers to, as declarations (none on a blank form). */
export function referencedAttachments(ctx, value) {
    if (ctx.mode === 'blank')
        return [];
    const ids = new Set(attachmentIds(value));
    return ctx.attachments.filter((a) => ids.has(a.id)).map(declOf);
}
/** Mirror of the assembler's numbering, only to order a multi-reference. */
function contextNumbers(ctx) {
    const taken = new Set();
    for (const a of ctx.attachments)
        if (a.fixedNumber != null)
            taken.add(a.fixedNumber);
    let next = 1;
    const numbers = new Map();
    for (const a of ctx.attachments) {
        if (a.fixedNumber != null) {
            numbers.set(a.id, a.fixedNumber);
            continue;
        }
        while (taken.has(next))
            next += 1;
        taken.add(next);
        numbers.set(a.id, next);
    }
    return numbers;
}
/**
 * "Bilaga 2" / "Bilaga 2 och Bilaga 4" / "Bilaga 2, Bilaga 4 och Bilaga 5"
 * as typed references in number order, or `null` when nothing resolves
 * (always on a blank form).
 */
export function annexRefs(ctx, value, display = 'number') {
    if (ctx.mode === 'blank')
        return null;
    const numbers = contextNumbers(ctx);
    const ids = attachmentIds(value)
        .filter((id) => numbers.has(id))
        .sort((a, b) => (numbers.get(a) ?? 0) - (numbers.get(b) ?? 0));
    if (ids.length === 0)
        return null;
    return ids.flatMap((id, i) => [...(i === 0 ? [] : [t(i === ids.length - 1 ? ' och ' : ', ')]), attachmentRef(id, display)]);
}
/** Resolved references, else a fill-in rule (legacy `attachmentLabel` placeholder). */
export const annexOrRule = (ctx, value, sourceKey, blankWidth = 'short') => annexRefs(ctx, value) ?? [val(null, { blankWidth, sourceKey })];
/** Resolved references, else "Bilaga ____" (legacy placeholder with its lead). */
export const annexOrBlank = (ctx, value, sourceKey) => annexRefs(ctx, value) ?? [t('Bilaga '), val(null, { blankWidth: 'short', sourceKey })];
/** The contract's bilaga list (legacy `AttachmentList`); a blank form or no bilagor prints the empty line. */
export const attachmentListNodes = (ctx, emptyText = 'Inga bilagor bifogade.') => ctx.attachments.length === 0 || ctx.mode === 'blank' ? [p(emptyText, { variant: 'fine_print' })] : [attachmentList()];
//# sourceMappingURL=attachments.js.map